import { useCallback, useMemo, useEffect, useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { Member } from '../types/member.types';
import MemberTable from '../components/table/member-table';
import { useMembers } from '../hooks/use-members';
import { useMemberSearchStore } from '../stores/member-search.store';
import {
  PageContainer,
  PageHeader,
  TitleSection,
  ActionButtonContainer,
  CreateMemberButton,
} from '../styles';

import { PageTitle } from '@/components/styles';
import { GlobalSearch } from '@/components/search';
import { Pagination } from '@/components/pagination';
import { getSearchConfig } from '@/config/search-configs';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { FunctionName, PermissionAction } from '@/lib/enums';
import { checkPermission } from '@/utils/helpers';

export const MembersPage = () => {
  const {
    searchResults,
    loading,
    currentPage,
    itemsPerPage,
    totalItems,
    selectedMembers,
    committedSearchParams,
    sortField,
    sortOrder,
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleSelectionChange,
    handleSortChange,
    handleDelete,
    handleRegisterAutocompleteClear,
  } = useMembers();

  // Get search state from store
  const searchInputValues = useMemberSearchStore((state) => state.searchInputValues);

  const navigate = useNavigate();
  const { setConfirmDialog } = useConfirmDialog();
  const { permissions } = useOidc();
  const [canCreateMember, setCanCreateMember] = useState<boolean>(false);
  const [canDeleteMember, setCanDeleteMember] = useState<boolean>(false);
  const [canUpdateMember, setCanUpdateMember] = useState<boolean>(false);
  // Handle navigate to member detail
  const handleNavigateToMemberDetail = useCallback(
    (member: Member) => {
      if (canUpdateMember) {
        navigate(`/members/${member.id}`);
      }
    },
    [canUpdateMember, navigate]
  );

  // Handle create new member
  const handleCreateMember = useCallback(() => {
    navigate('/members/register');
  }, []);

  useEffect(() => {
    // get permissions list user in OIDC
    setCanCreateMember(
      checkPermission(permissions, FunctionName.STAFF_MANAGEMENT, PermissionAction.CREATE)
    );
    setCanDeleteMember(
      checkPermission(permissions, FunctionName.STAFF_MANAGEMENT, PermissionAction.DELETE)
    );
    setCanUpdateMember(
      checkPermission(permissions, FunctionName.STAFF_MANAGEMENT, PermissionAction.UPDATE)
    );
  }, [permissions]);

  // Handle delete selected members
  const handleDeleteSelected = useCallback(() => {
    if (selectedMembers.length === 0) return;
    setConfirmDialog({
      title: `${selectedMembers.length}件の担当者を削除しますか？`,
      confirmDialog: () => {
        void handleDelete(selectedMembers);
      },
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
    });
  }, [selectedMembers, handleDelete]);

  const searchConfig = useMemo(
    () => ({
      ...getSearchConfig('members'),
      initialValues: searchInputValues, // Pass initial values from store to restore form fields
      onSearch: (params: Record<string, unknown>) => {
        handleSearch({ ...params, sort: sortField, order_by: sortOrder });
      },
      onChange: (params: Record<string, unknown>) => {
        handleSearchChange(params);
      },
      onClear: handleClear,
      loading,
    }),
    [
      handleSearch,
      handleSearchChange,
      handleClear,
      loading,
      sortField,
      sortOrder,
      searchInputValues,
    ]
  );

  // Restore search when navigating back - trigger search if there are committed params
  useEffect(() => {
    // Trigger search if there are committed params (form fields are restored via initialValues)
    // Preserve page number when restoring
    if (Object.keys(committedSearchParams).length > 0) {
      handleSearch(committedSearchParams, true); // Preserve page number
    }
  }, []);

  return (
    <PageContainer>
      <PageHeader>
        <TitleSection>
          <PageTitle variant='h6'>担当者一覧</PageTitle>
        </TitleSection>
        {canCreateMember && (
          <ActionButtonContainer>
            <CreateMemberButton
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleCreateMember}
            >
              担当者登録
            </CreateMemberButton>
          </ActionButtonContainer>
        )}
      </PageHeader>

      <GlobalSearch config={searchConfig} clearAutocomplete={handleRegisterAutocompleteClear} />

      {/* Pagination with Delete Button - specific to members page */}
      <Box sx={{ mb: 1.5 }}>
        <Pagination
          currentPage={currentPage - 1}
          totalPages={Math.max(1, Math.ceil(totalItems / itemsPerPage))}
          rowsPerPage={itemsPerPage}
          totalCount={totalItems}
          onPageChange={(page) => handlePageChange(page + 1)}
          onRowsPerPageChange={handleItemsPerPageChange}
          rowsPerPageOptions={[20, 50, 100]}
          showDeleteButton={canDeleteMember}
          selectedCount={selectedMembers.length}
          onDelete={handleDeleteSelected}
        />
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <MemberTable
          data={searchResults}
          loading={loading}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          selectedMembers={selectedMembers}
          onSelectionChange={handleSelectionChange}
          onMemberClick={handleNavigateToMemberDetail}
          filterStatus={
            typeof committedSearchParams?.status === 'string'
              ? committedSearchParams.status
              : undefined
          }
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
      </Box>
    </PageContainer>
  );
};
